/**
 * Authentication Store
 *
 * Zustand store for managing authentication state.
 * Persists tokens to localStorage for session recovery.
 *
 * SECURITY NOTE - Token Storage:
 * - Current: accessToken + refreshToken stored in localStorage
 * - Risk: Vulnerable to XSS attacks if malicious scripts access localStorage
 * - Trade-off: Chosen for ease of implementation and better UX (persistent sessions)
 * - Production Recommendation: Move refreshToken to HttpOnly cookies
 *   - Backend sets: `Set-Cookie: refreshToken=xxx; HttpOnly; Secure; SameSite=Strict`
 *   - Frontend keeps accessToken in memory or short-lived localStorage
 *   - XSS cannot steal HttpOnly cookies
 * - Mitigation: Implement Content Security Policy (CSP) headers to prevent XSS
 * - Future Enhancement: Token rotation on every refresh for additional security
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface AuthUser {
  // Web3Auth info
  wallet?: string;
  email?: string;
  name?: string;
  profileImage?: string;
  typeOfLogin?: string;
  verifierId?: string;
  idToken?: string;

  // SOKA API profile info
  id?: number;
  pseudo?: string;
  points?: number;
  country?: string;
}

interface AuthState {
  // State
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  web3AuthIdToken: string | null;
  error: string | null;

  // Actions
  setUser: (user: AuthUser) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setWeb3AuthIdToken: (idToken: string) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  logout: () => void;
  reset: () => void;
}

const initialState = {
  isAuthenticated: false,
  isLoading: false,
  user: null,
  accessToken: null,
  refreshToken: null,
  web3AuthIdToken: null,
  error: null
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      ...initialState,

      setUser: (user) =>
        set({
          user,
          isAuthenticated: true,
          error: null
        }),

      setTokens: (accessToken, refreshToken) =>
        set({
          accessToken,
          refreshToken
        }),

      setWeb3AuthIdToken: (idToken) =>
        set({
          web3AuthIdToken: idToken
        }),

      setLoading: (isLoading) => set({ isLoading }),

      setError: (error) =>
        set({
          error,
          isLoading: false
        }),

      logout: () =>
        set({
          ...initialState
        }),

      reset: () => set(initialState)
    }),
    {
      name: 'ludoka-auth',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        web3AuthIdToken: state.web3AuthIdToken
      })
    }
  )
);

// Selectors for common use cases
export const selectIsAuthenticated = (state: AuthState) => state.isAuthenticated;
export const selectUser = (state: AuthState) => state.user;
export const selectAccessToken = (state: AuthState) => state.accessToken;
export const selectIsLoading = (state: AuthState) => state.isLoading;
export const selectError = (state: AuthState) => state.error;
